import type { ReactNode } from "react";
import { AI_VERSION_PATH } from "../lib/cvMarkdown";
import { calculateAge } from "../lib/utils";

interface PersonalInfoProps {
  personal_info: {
    name: string;
    affiliation: string;
    birthday: string;
    gender: string;
    nearest_station: string;
    education: string;
    qualifications: string;
    role: string;
    expertise: string;
    links: {
      github: string;
      facebook: string;
      resume: string;
    };
  };
}

export function PersonalInfo({ personal_info }: PersonalInfoProps) {
  const links = [
    { label: "GitHub", href: personal_info.links.github },
    { label: "Facebook", href: personal_info.links.facebook },
    { label: "Web版", href: personal_info.links.resume },
    { label: "AI向け", href: AI_VERSION_PATH, printHidden: true },
  ];

  const today = new Date();

  return (
    <header className="flex flex-col gap-8 print:gap-5">
      <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
        <h1 className="jp-heading text-5xl sm:text-6xl print:text-4xl font-black leading-none tracking-[0.06em] text-ink">
          {personal_info.name}
        </h1>
        <div className="sm:text-right">
          <p className="text-base font-bold tracking-[0.12em] text-ink-2">
            職務経歴書
          </p>
          <p className="hidden print:block mt-1 text-xs text-ink-3">
            {today.getFullYear()}年{today.getMonth() + 1}月{today.getDate()}日現在
          </p>
        </div>
      </div>

      <section>
        <h2 className="sr-only">基本情報</h2>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-rule border-[1.5px] border-ink">
          <Field label="所属">{personal_info.affiliation}</Field>
          <Field label="年齢">{calculateAge(personal_info.birthday)}</Field>
          <Field label="性別">{personal_info.gender}</Field>
          <Field label="最寄駅">{personal_info.nearest_station}</Field>
          <Field label="学歴" className="col-span-2">
            {personal_info.education}
          </Field>
          <Field label="資格" className="col-span-2">
            {personal_info.qualifications}
          </Field>
          <Field label="役割" className="col-span-2">
            {personal_info.role}
          </Field>
          <Field label="専門分野" className="col-span-2 sm:col-span-1 print:col-span-2">
            {personal_info.expertise}
          </Field>
          <Field label="リンク" className="col-span-2 sm:col-span-1 print:col-span-4">
            <span className="flex flex-wrap gap-x-4 print:gap-x-6">
              {links.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-accent underline decoration-accent/30 underline-offset-4 hover:decoration-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${link.printHidden ? "print:hidden" : ""}`}
                >
                  {link.label}
                  <span className="hidden print:inline">
                    {" "}
                    {link.href.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  </span>
                </a>
              ))}
            </span>
          </Field>
        </dl>
      </section>
    </header>
  );
}

function Field({
  label,
  className = "",
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`bg-white px-4 py-3 print:px-3 print:py-2 ${className}`}>
      <dt className="text-xs font-medium text-ink-3">{label}</dt>
      <dd className="mt-1 print:mt-0.5 text-[15px] print:text-[13.5px] font-medium leading-relaxed text-ink">
        {children}
      </dd>
    </div>
  );
}
